import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GitBranchPlus, Play, Plus, TriangleAlert } from "lucide-react";
import { Background, Controls, MarkerType, ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";


const TYPES = {
  CLOUD: "Cloud", SAAS: "SaaS", SOFTWARE: "Éditeur logiciel", OPEN_SOURCE: "Open source",
  NETWORK: "Réseau / télécom", IDENTITY: "Identité", SECURITY_SERVICE: "Service de sécurité",
  DATA_PROCESSOR: "Sous-traitant de données", PAYMENT: "Paiement", OUTSOURCER: "Infogérant", OTHER: "Autre",
};
const CRITICALITY = { critical: "Critique", high: "Élevée", medium: "Moyenne", low: "Faible" };
const EMPTY = {
  name: "", supplier_type: "OTHER", status: "UNDER_REVIEW", criticality: "medium",
  country: "", jurisdiction: "", owner: "", description: "", contract_reference: "", exit_strategy: "",
};
const EMPTY_DEPENDENCY = {
  source_type: "service", source_id: "", supplier_id: "", dependency_type: "OTHER",
  criticality: "medium", confidence: "UNKNOWN", substitutability: "UNKNOWN",
  replacement_days: "", evidence_reference: "", description: "",
};
const CONFIDENCE = { VERIFIED: "Vérifiée", DECLARED: "Déclarée", INFERRED: "Inférée", UNKNOWN: "Inconnue" };
const SUBSTITUTABILITY = { EASY: "Facile", DIFFICULT: "Difficile", IMPOSSIBLE: "Impossible", UNKNOWN: "Inconnue" };


function Metric({ label, value, tone = "text-foreground" }) {
  return (
    <div className="border border-border rounded-sm bg-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
      <div className={`mt-2 text-2xl font-head font-bold ${tone}`}>{value}</div>
    </div>
  );
}


export default function SupplyChain() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dependencyOpen, setDependencyOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [dependencyForm, setDependencyForm] = useState(EMPTY_DEPENDENCY);
  const [rootKey, setRootKey] = useState("");
  const [simulationSupplier, setSimulationSupplier] = useState("");
  const [incident, setIncident] = useState("UNAVAILABLE");
  const writable = can("supply_chain:write");

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ["supply-chain-suppliers"],
    queryFn: async () => (await api.get("/supply-chain/suppliers")).data,
  });
  const { data: concentrations = [] } = useQuery({
    queryKey: ["supply-chain-concentrations"],
    queryFn: async () => (await api.get("/supply-chain/concentrations")).data,
  });
  const { data: graph = { nodes: [] } } = useQuery({
    queryKey: ["graph"],
    queryFn: async () => (await api.get("/graph")).data,
  });
  const root = graph.nodes.find((node) => `${node.type}:${node.id}` === rootKey);
  const { data: mapping } = useQuery({
    queryKey: ["supply-chain-map", rootKey],
    enabled: !!root,
    queryFn: async () => (await api.get(`/supply-chain/map/${root.type}/${root.id}`)).data,
  });
  const create = useMutation({
    mutationFn: async () => (await api.post("/supply-chain/suppliers", form)).data,
    onSuccess: () => {
      toast.success("Fournisseur ajouté");
      setOpen(false);
      setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["supply-chain-suppliers"] });
      qc.invalidateQueries({ queryKey: ["supply-chain-concentrations"] });
    },
    onError: (error) => toast.error(formatApiError(error.response?.data?.detail)),
  });
  const createDependency = useMutation({
    mutationFn: async () => {
      const payload = { ...dependencyForm };
      payload.replacement_days = payload.replacement_days === "" ? null : Number(payload.replacement_days);
      return (await api.post("/supply-chain/dependencies", payload)).data;
    },
    onSuccess: () => {
      toast.success("Dépendance ajoutée");
      setDependencyOpen(false);
      setDependencyForm(EMPTY_DEPENDENCY);
      qc.invalidateQueries({ queryKey: ["supply-chain-suppliers"] });
      qc.invalidateQueries({ queryKey: ["supply-chain-map"] });
      qc.invalidateQueries({ queryKey: ["supply-chain-concentrations"] });
    },
    onError: (error) => toast.error(formatApiError(error.response?.data?.detail)),
  });
  const simulation = useMutation({
    mutationFn: async () => (await api.post(`/supply-chain/suppliers/${simulationSupplier}/simulate`, {
      incident, max_rank: 4,
    })).data,
    onError: (error) => toast.error(formatApiError(error.response?.data?.detail)),
  });

  const flow = useMemo(() => buildFlow(root, mapping), [root, mapping]);

  const critical = suppliers.filter((supplier) => supplier.criticality === "critical").length;
  const underReview = suppliers.filter((supplier) => supplier.status === "UNDER_REVIEW").length;

  return (
    <AppShell
      title="Supply chain resilience"
      subtitle="Dépendances fournisseurs directes et indirectes · rangs 1 à 4"
      actions={writable && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={() => setDependencyOpen(true)} data-testid="create-dependency-button"><GitBranchPlus className="h-4 w-4" /> Nouvelle dépendance</Button>
          <Button size="sm" className="rounded-sm gap-2" onClick={() => setOpen(true)} data-testid="create-supplier-button"><Plus className="h-4 w-4" /> Nouveau fournisseur</Button>
        </div>
      )}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Metric label="Fournisseurs" value={suppliers.length} />
        <Metric label="Critiques" value={critical} tone={critical ? "text-red-400" : "text-green-400"} />
        <Metric label="À qualifier" value={underReview} tone={underReview ? "text-orange-400" : "text-green-400"} />
        <Metric label="Concentrations" value={concentrations.length} tone={concentrations.length ? "text-orange-400" : "text-green-400"} />
      </div>

      {concentrations.length > 0 && (
        <div className="mb-6 border border-orange-500/30 bg-orange-500/5 rounded-sm p-4" data-testid="supply-concentrations">
          <div className="flex items-center gap-2 text-sm font-medium text-orange-300 mb-3">
            <TriangleAlert className="h-4 w-4" /> Concentrations de dépendances
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {concentrations.slice(0, 6).map((item) => (
              <div key={item.supplier_id} className="flex justify-between border border-border bg-background/50 p-3 rounded-sm text-sm">
                <span>{item.supplier_name}</span>
                <Badge variant="outline" className="rounded-sm">{item.affected_root_count} services</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <div className="xl:col-span-2 border border-border rounded-sm bg-card p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
            <div><h2 className="font-head font-semibold">Cartographie des rangs 1 à 4</h2><p className="text-xs text-muted-foreground">Choisissez un service ou un actif pour afficher sa chaîne de dépendances.</p></div>
            <Select value={rootKey} onValueChange={setRootKey}><SelectTrigger className="md:w-72"><SelectValue placeholder="Choisir une racine métier" /></SelectTrigger><SelectContent>{graph.nodes.filter((node) => ["service", "asset"].includes(node.type)).map((node) => <SelectItem key={node.id} value={`${node.type}:${node.id}`}>{node.name} · {node.type === "service" ? "Service" : "Actif"}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="h-[380px] border border-border bg-background/40 rounded-sm" data-testid="supply-chain-map">
            {root ? <ReactFlow nodes={flow.nodes} edges={flow.edges} fitView nodesDraggable={false} nodesConnectable={false} elementsSelectable><Background /><Controls /></ReactFlow> : <div className="h-full grid place-items-center text-sm text-muted-foreground">Sélectionnez une racine métier.</div>}
          </div>
          {mapping && <div className="grid grid-cols-3 gap-2 mt-3 text-center"><Metric label="Visibilité" value={`${mapping.metrics.visibility_score}%`} /><Metric label="Dépendances" value={mapping.metrics.dependency_count} /><Metric label="Non substituables" value={mapping.metrics.non_substitutable_count} tone={mapping.metrics.non_substitutable_count ? "text-red-400" : "text-green-400"} /></div>}
        </div>

        <div className="border border-border rounded-sm bg-card p-4">
          <h2 className="font-head font-semibold">Simulation fournisseur</h2>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Projection sans modification des données CROS.</p>
          <Field label="Fournisseur"><Select value={simulationSupplier} onValueChange={setSimulationSupplier}><SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{suppliers.map((supplier) => <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>)}</SelectContent></Select></Field>
          <div className="mt-3"><Field label="Incident"><Select value={incident} onValueChange={setIncident}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="UNAVAILABLE">Indisponibilité</SelectItem><SelectItem value="COMPROMISED">Compromission</SelectItem></SelectContent></Select></Field></div>
          <Button className="w-full mt-4 rounded-sm gap-2" disabled={!simulationSupplier || simulation.isPending} onClick={() => simulation.mutate()}><Play className="h-4 w-4" /> {simulation.isPending ? "Simulation…" : "Simuler l’impact"}</Button>
          {simulation.data && <div className="mt-4 border border-border rounded-sm p-3" data-testid="supplier-simulation-result"><div className="text-3xl font-head font-bold">{simulation.data.metrics.affected_root_count}</div><div className="text-xs text-muted-foreground mb-3">services ou actifs affectés</div><div className="space-y-2 max-h-48 overflow-auto">{simulation.data.affected_roots.map((item) => <div key={`${item.root.type}:${item.root.id}`} className="border-l-2 border-red-400 pl-2"><div className="text-sm">{item.root.name}</div><div className="text-[10px] text-muted-foreground">Rang {item.supplier_rank} · {item.impact === "UNAVAILABLE" ? "Indisponible" : "À risque"}</div></div>)}{simulation.data.affected_roots.length === 0 && <p className="text-xs text-green-400">Aucune racine exposée dans les quatre premiers rangs.</p>}</div></div>}
        </div>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground font-mono">Chargement…</p> : (
        <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="suppliers-table">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent border-border">
              <TableHead>Fournisseur</TableHead><TableHead>Type</TableHead><TableHead>Criticité</TableHead>
              <TableHead>Pays / juridiction</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Dépendances</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {suppliers.map((supplier) => (
                <TableRow key={supplier.id} className="border-border" data-testid={`supplier-row-${supplier.id}`}>
                  <TableCell><div className="font-medium">{supplier.name}</div><div className="text-xs text-muted-foreground">{supplier.owner || "Responsable non renseigné"}</div></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{TYPES[supplier.supplier_type] || supplier.supplier_type}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-[10px]">{CRITICALITY[supplier.criticality]}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{supplier.country || "—"}{supplier.jurisdiction ? ` · ${supplier.jurisdiction}` : ""}</TableCell>
                  <TableCell className="text-xs">{supplier.status}</TableCell>
                  <TableCell className="text-right font-mono">{supplier.dependency_count || 0}</TableCell>
                </TableRow>
              ))}
              {suppliers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Aucun fournisseur cartographié.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-xl" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>Nouveau fournisseur</DialogTitle><DialogDescription>Créez le tiers avant de rattacher ses dépendances aux services, actifs ou autres fournisseurs.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Nom" className="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></Field>
            <Field label="Type"><Select value={form.supplier_type} onValueChange={(value) => setForm({ ...form, supplier_type: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(TYPES).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Criticité"><Select value={form.criticality} onValueChange={(value) => setForm({ ...form, criticality: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CRITICALITY).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Pays"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
            <Field label="Juridiction"><Input value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} /></Field>
            <Field label="Responsable"><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></Field>
            <Field label="Référence contrat"><Input value={form.contract_reference} onChange={(e) => setForm({ ...form, contract_reference: e.target.value })} /></Field>
            <Field label="Description" className="col-span-2"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => create.mutate()} disabled={!form.name || create.isPending} data-testid="save-supplier-button">{create.isPending ? "Enregistrement…" : "Enregistrer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dependencyOpen} onOpenChange={setDependencyOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="dependency-dialog">
          <DialogHeader><DialogTitle>Nouvelle dépendance fournisseur</DialogTitle><DialogDescription>Reliez un service, un actif ou un fournisseur à son prestataire direct.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Type de source"><Select value={dependencyForm.source_type} onValueChange={(value) => setDependencyForm({ ...dependencyForm, source_type: value, source_id: "" })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="service">Service</SelectItem><SelectItem value="asset">Actif</SelectItem><SelectItem value="supplier">Fournisseur</SelectItem></SelectContent></Select></Field>
            <Field label="Source"><Select value={dependencyForm.source_id} onValueChange={(value) => setDependencyForm({ ...dependencyForm, source_id: value })}><SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{sourceChoices(graph.nodes, suppliers, dependencyForm.source_type).map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Fournisseur cible"><Select value={dependencyForm.supplier_id} onValueChange={(value) => setDependencyForm({ ...dependencyForm, supplier_id: value })}><SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{suppliers.filter((supplier) => supplier.id !== dependencyForm.source_id).map((supplier) => <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Criticité"><Select value={dependencyForm.criticality} onValueChange={(value) => setDependencyForm({ ...dependencyForm, criticality: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CRITICALITY).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Niveau de preuve"><Select value={dependencyForm.confidence} onValueChange={(value) => setDependencyForm({ ...dependencyForm, confidence: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CONFIDENCE).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Substituabilité"><Select value={dependencyForm.substitutability} onValueChange={(value) => setDependencyForm({ ...dependencyForm, substitutability: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(SUBSTITUTABILITY).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Délai de remplacement (jours)"><Input type="number" min="0" value={dependencyForm.replacement_days} onChange={(e) => setDependencyForm({ ...dependencyForm, replacement_days: e.target.value })} /></Field>
            <Field label="Référence de preuve"><Input value={dependencyForm.evidence_reference} onChange={(e) => setDependencyForm({ ...dependencyForm, evidence_reference: e.target.value })} /></Field>
            <Field label="Description" className="col-span-2"><Textarea rows={2} value={dependencyForm.description} onChange={(e) => setDependencyForm({ ...dependencyForm, description: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDependencyOpen(false)}>Annuler</Button><Button onClick={() => createDependency.mutate()} disabled={!dependencyForm.source_id || !dependencyForm.supplier_id || createDependency.isPending}>{createDependency.isPending ? "Enregistrement…" : "Créer la dépendance"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


function Field({ label, className = "", children }) {
  return <div className={className}><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

function sourceChoices(nodes, suppliers, type) {
  return type === "supplier" ? suppliers : nodes.filter((node) => node.type === type);
}

function buildFlow(root, mapping) {
  if (!root || !mapping) return { nodes: [], edges: [] };
  const nodes = [{ id: `root:${root.id}`, data: { label: `${root.name}\nRacine` }, position: { x: 0, y: 140 }, style: flowStyle("root") }];
  mapping.suppliers.forEach((supplier) => {
    const sameRank = mapping.suppliers.filter((item) => item.rank === supplier.rank);
    const rankIndex = sameRank.findIndex((item) => item.id === supplier.id);
    nodes.push({ id: `supplier:${supplier.id}`, data: { label: `${supplier.name}\nRang ${supplier.rank}` }, position: { x: supplier.rank * 240, y: rankIndex * 110 + 40 }, style: flowStyle(supplier.criticality) });
  });
  const edges = mapping.dependencies.map((dep) => ({
    id: dep.id,
    source: dep.source_type === "supplier" ? `supplier:${dep.source_id}` : `root:${root.id}`,
    target: `supplier:${dep.supplier_id}`,
    label: CONFIDENCE[dep.confidence] || dep.confidence,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: dep.criticality === "critical" ? "#ef4444" : "#64748b" },
  })).filter((edge) => nodes.some((node) => node.id === edge.source) && nodes.some((node) => node.id === edge.target));
  return { nodes, edges };
}

function flowStyle(tone) {
  const border = tone === "critical" ? "#ef4444" : tone === "root" ? "#3b82f6" : "#64748b";
  return { width: 180, whiteSpace: "pre-line", textAlign: "center", border: `1px solid ${border}`, borderRadius: 4, background: "hsl(var(--card))", color: "hsl(var(--foreground))", fontSize: 12 };
}
